import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, ScrollView, Platform, Switch, Dimensions } from 'react-native';
import { db, sendPasswordReset } from './firebaseConfig';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, serverTimestamp, setDoc, getDoc } from 'firebase/firestore';
import LegBetCard from './components/LegBetCard';
import CacheMonitoringTab from './components/CacheMonitoringTab';
import SGPCacheTab from './components/SGPCacheTab';
import SiteHealthTab from './components/SiteHealthTab';
import EmailTestingTab from './components/EmailTestingTab';
import GameTicker from './components/GameTicker';
import { useTicker } from './context/TickerContext';
import { useNavigation } from './context/NavigationContext';
import Constants from 'expo-constants';

function TabButton({ label, active, onPress }) {
  return (
    <TouchableOpacity onPress={onPress} style={[styles.tabBtn, active ? styles.tabBtnActive : null]}>
      <Text style={[styles.tabBtnText, active ? styles.tabBtnTextActive : null]}>{label}</Text>
    </TouchableOpacity>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPlan, setNewUserPlan] = useState('FREE');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserSuspended, setNewUserSuspended] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Responsive layout
  const windowWidth = Dimensions.get('window').width;
  const isTablet = windowWidth >= 768;
  const isMobile = windowWidth < 768;
  
  const toggleGroup = (userId, groupName) => {
    const key = `${userId}_${groupName}`;
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setUsers(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const setRole = async (uid, role) => {
    try {
      await updateDoc(doc(db, 'users', uid), { userType: role, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set role', e);
    }
  };

  const setPlan = async (uid, planType, status = 'active') => {
    try {
      await updateDoc(doc(db, 'users', uid), { subscription: { planType, subscriptionStatus: status, subscriptionUpdatedAt: new Date().toISOString() }, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set plan', e);
    }
  };

  const setSuspended = async (uid, suspended) => {
    try {
      await updateDoc(doc(db, 'users', uid), { suspended: !!suspended, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set suspended', e);
    }
  };

  const setAlphaAccess = async (uid, hasAccess) => {
    try {
      await updateDoc(doc(db, 'users', uid), { alphaAccess: !!hasAccess, updatedAt: serverTimestamp() });
    } catch (e) {
      console.warn('Failed to set alpha access', e);
    }
  };

  const createUserDoc = async () => {
    try {
      const base = (
        (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
        || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
        || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
      );
      if (!base) throw new Error('Functions base not configured');
      const payload = {
        email: newUserEmail.trim(),
        role: newUserRole,
        planType: newUserPlan,
        suspended: !!newUserSuspended,
        password: newUserPassword.trim() || undefined
      };
      const res = await fetch(`${base}/adminCreateUserAndInvite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      setNewUserEmail(''); setNewUserPassword(''); setNewUserPlan('FREE'); setNewUserRole('user'); setNewUserSuspended(false); setModalOpen(false);
    } catch (e) {
      console.warn('Failed to create user doc', e);
    }
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Users {loading ? '(loading...)' : `(${users.length})`}</Text>
      <View style={[styles.row, { alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }]}> 
        <TouchableOpacity style={styles.btn} onPress={() => setModalOpen(true)}><Text style={styles.btnText}>Add New User</Text></TouchableOpacity>
      </View>
      {modalOpen ? (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <View style={{ width: 520, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', padding: 16 }}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 18, marginBottom: 10 }}>Create New User</Text>
            <View style={[styles.row, { marginBottom: 8 }]}>
              <Text style={styles.label}>Email</Text>
              <TextInput style={styles.input} value={newUserEmail} onChangeText={setNewUserEmail} placeholder="email@domain.com" placeholderTextColor="rgba(255,255,255,0.5)" />
            </View>
            <View style={[styles.row, { marginBottom: 8 }]}> 
              <Text style={styles.label}>Plan</Text>
              <View style={[styles.input, { paddingVertical: 8 }]}> 
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {['FREE','BASIC','PRO','ULTIMATE'].map(p => (
                    <TouchableOpacity key={p} onPress={() => setNewUserPlan(p)} style={[styles.tag, newUserPlan===p ? styles.tagActive : null]}>
                      <Text style={[styles.tagText, newUserPlan===p ? styles.tagTextActive : null]}>{p}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
            <View style={[styles.row, { marginBottom: 8 }]}> 
              <Text style={styles.label}>Role</Text>
              <View style={[styles.input, { paddingVertical: 8 }]}> 
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {['user','admin'].map(r => (
                    <TouchableOpacity key={r} onPress={() => setNewUserRole(r)} style={[styles.tag, newUserRole===r ? styles.tagActive : null]}>
                      <Text style={[styles.tagText, newUserRole===r ? styles.tagTextActive : null]}>{r}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
            <View style={[styles.row, { marginBottom: 8 }]}>
              <Text style={styles.label}>Suspended</Text>
              <Switch value={newUserSuspended} onValueChange={setNewUserSuspended} />
            </View>
            <View style={[styles.row, { marginBottom: 8 }]}>
              <Text style={styles.label}>Password (optional)</Text>
              <TextInput style={styles.input} value={newUserPassword} onChangeText={setNewUserPassword} placeholder="Temporary password" placeholderTextColor="rgba(255,255,255,0.5)" />
            </View>
            <View style={[styles.row, { justifyContent: 'flex-end', gap: 8 }]}> 
              <TouchableOpacity style={[styles.smallBtn, { backgroundColor: 'rgba(255,255,255,0.1)' }]} onPress={() => setModalOpen(false)}><Text style={styles.smallBtnText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.btn} onPress={createUserDoc}><Text style={styles.btnText}>Create</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      ) : null}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {users.map((u) => {
          const isExpanded = expandedUsers[u.id];
          const roleGroupExpanded = expandedGroups[`${u.id}_role`];
          const planGroupExpanded = expandedGroups[`${u.id}_plan`];
          const statusGroupExpanded = expandedGroups[`${u.id}_status`];
          
          return (
            <View key={u.id} style={[styles.userRow, isMobile && styles.userRowMobile]}>
              {/* User Info Section - Always at Top */}
              <View style={{ width: '100%' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 8 : 0 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.userEmail, isMobile && { fontSize: 14, marginBottom: 4 }]}>{u.email || u.id}</Text>
                    <Text style={[styles.userMeta, isMobile && { fontSize: 11, marginBottom: 2 }]}>
                      role: {u.userType || 'user'}{u.displayName ? ` • ${u.displayName}` : ''}
                    </Text>
                    <Text style={[styles.userMeta, isMobile && { fontSize: 11 }]}>
                      plan: {u?.subscription?.planType || 'FREE'} • status: {u?.subscription?.subscriptionStatus || 'none'} 
                      {u.suspended ? ' • SUSPENDED' : ''} 
                      {u.alphaAccess ? ' • ✅ ALPHA' : ''}
                    </Text>
                  </View>
                  {isMobile && (
                    <TouchableOpacity 
                      onPress={() => setExpandedUsers(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                      style={{ padding: 8 }}
                    >
                      <Text style={{ color: '#fff', fontSize: 18 }}>{isExpanded ? '▲' : '▼'}</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              
              {/* Actions Section */}
              {(!isMobile || isExpanded) && (
                <View style={[
                  styles.userActions, 
                  isMobile && { 
                    width: '100%', 
                    marginTop: 12, 
                    paddingTop: 12, 
                    borderTopWidth: 1, 
                    borderTopColor: 'rgba(255,255,255,0.1)',
                    flexDirection: 'column',
                    gap: 0
                  }
                ]}>
                  {isMobile ? (
                    <>
                      {/* Mobile: Dropdown Style Groups */}
                      {/* Role Dropdown */}
                      <View style={styles.mobileDropdownGroup}>
                        <TouchableOpacity 
                          style={styles.mobileDropdownHeader}
                          onPress={() => toggleGroup(u.id, 'role')}
                        >
                          <Text style={styles.mobileDropdownHeaderText}>Role: {u.userType || 'user'}</Text>
                          <Text style={styles.mobileDropdownArrow}>{roleGroupExpanded ? '▲' : '▼'}</Text>
                        </TouchableOpacity>
                        {roleGroupExpanded && (
                          <View style={styles.mobileDropdownContent}>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setRole(u.id, 'admin'); toggleGroup(u.id, 'role'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>👤 Admin</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setRole(u.id, 'user'); toggleGroup(u.id, 'role'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>👤 User</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                      
                      {/* Plan Dropdown */}
                      <View style={styles.mobileDropdownGroup}>
                        <TouchableOpacity 
                          style={styles.mobileDropdownHeader}
                          onPress={() => toggleGroup(u.id, 'plan')}
                        >
                          <Text style={styles.mobileDropdownHeaderText}>Plan: {u?.subscription?.planType || 'FREE'}</Text>
                          <Text style={styles.mobileDropdownArrow}>{planGroupExpanded ? '▲' : '▼'}</Text>
                        </TouchableOpacity>
                        {planGroupExpanded && (
                          <View style={styles.mobileDropdownContent}>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setPlan(u.id, 'FREE', 'none'); toggleGroup(u.id, 'plan'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>🆓 FREE</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setPlan(u.id, 'BASIC', 'active'); toggleGroup(u.id, 'plan'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>📦 BASIC</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setPlan(u.id, 'PRO', 'active'); toggleGroup(u.id, 'plan'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>⭐ PRO</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setPlan(u.id, 'ULTIMATE', 'active'); toggleGroup(u.id, 'plan'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>💎 ULTIMATE</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                      
                      {/* Status Dropdown */}
                      <View style={styles.mobileDropdownGroup}>
                        <TouchableOpacity 
                          style={styles.mobileDropdownHeader}
                          onPress={() => toggleGroup(u.id, 'status')}
                        >
                          <Text style={styles.mobileDropdownHeaderText}>
                            Status: {u.suspended ? 'SUSPENDED' : 'Active'}{u.alphaAccess ? ' • Alpha' : ''}
                          </Text>
                          <Text style={styles.mobileDropdownArrow}>{statusGroupExpanded ? '▲' : '▼'}</Text>
                        </TouchableOpacity>
                        {statusGroupExpanded && (
                          <View style={styles.mobileDropdownContent}>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setSuspended(u.id, !u.suspended); toggleGroup(u.id, 'status'); }}
                            >
                              <Text style={styles.mobileDropdownItemText}>
                                {u.suspended ? '✅ Unsuspend' : '🚫 Suspend'}
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={() => { setAlphaAccess(u.id, !u.alphaAccess); toggleGroup(u.id, 'status'); }}
                            >
                              <Text style={[styles.mobileDropdownItemText, u.alphaAccess && { color: '#4ade80' }]}>
                                {u.alphaAccess ? '✅ Remove Alpha' : '🌟 Grant Alpha'}
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.mobileDropdownItem} 
                              onPress={async () => {
                                if (!u.email) {
                                  alert('No email address for this user');
                                  return;
                                }
                                try {
                                  await sendPasswordReset(u.email);
                                  alert(`Password reset email sent to ${u.email}`);
                                  toggleGroup(u.id, 'status');
                                } catch (error) {
                                  console.error('Reset password error:', error);
                                  alert(`Error: ${error.message || 'Failed to send reset email'}`);
                                }
                              }}
                            >
                              <Text style={[styles.mobileDropdownItemText, { color: '#fbbf24' }]}>
                                🔑 Send Reset Password Email
                              </Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                    </>
                  ) : (
                    <>
                      {/* Desktop: Inline Buttons */}
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setRole(u.id, 'admin')}>
                        <Text style={styles.smallBtnText}>Make Admin</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setRole(u.id, 'user')}>
                        <Text style={styles.smallBtnText}>Make User</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setPlan(u.id, 'FREE', 'none')}>
                        <Text style={styles.smallBtnText}>Set FREE</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setPlan(u.id, 'BASIC', 'active')}>
                        <Text style={styles.smallBtnText}>Set BASIC</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setPlan(u.id, 'PRO', 'active')}>
                        <Text style={styles.smallBtnText}>Set PRO</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setPlan(u.id, 'ULTIMATE', 'active')}>
                        <Text style={styles.smallBtnText}>Set ULTIMATE</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.smallBtn} onPress={() => setSuspended(u.id, !u.suspended)}>
                        <Text style={styles.smallBtnText}>{u.suspended ? 'Unsuspend' : 'Suspend'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={[styles.smallBtn, u.alphaAccess && { backgroundColor: 'rgba(74, 222, 128, 0.2)', borderWidth: 1, borderColor: '#4ade80' }]} 
                        onPress={() => setAlphaAccess(u.id, !u.alphaAccess)}
                      >
                        <Text style={[styles.smallBtnText, u.alphaAccess && { color: '#4ade80' }]}>
                          {u.alphaAccess ? '✅ Alpha' : 'Grant Alpha'}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={[styles.smallBtn, { backgroundColor: 'rgba(251, 191, 36, 0.2)', borderWidth: 1, borderColor: '#fbbf24' }]} 
                        onPress={async () => {
                          if (!u.email) {
                            alert('No email address for this user');
                            return;
                          }
                          try {
                            await sendPasswordReset(u.email);
                            alert(`Password reset email sent to ${u.email}`);
                          } catch (error) {
                            console.error('Reset password error:', error);
                            alert(`Error: ${error.message || 'Failed to send reset email'}`);
                          }
                        }}
                      >
                        <Text style={[styles.smallBtnText, { color: '#fbbf24' }]}>
                          🔑 Reset Password
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

async function runOpticOddsRequest(path) {
  const functionsBase = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
      || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  if (!(Platform.OS === 'web' && !!functionsBase)) {
    return { status: 500, ok: false, body: { error: 'Functions base not configured. Set EXPO_PUBLIC_FUNCTIONS_BASE_URL.' } };
  }
  const url = `${functionsBase}/opticOddsProxy?path=${encodeURIComponent(path)}`;
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  const text = await res.text();
  let body;
  try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

async function runNlOdds(prompt, defaults = {}) {
  const base = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  const url = `${base}/nlOdds`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, defaults })
  });
  const text = await res.text();
  let body; try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

async function runSweep(params = {}) {
  const base = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );
  const url = `${base}/opticOddsSweep`;
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(params) });
  const text = await res.text();
  let body; try { body = JSON.parse(text); } catch { body = text; }
  return { status: res.status, ok: res.ok, body };
}

function ApiTab() {
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [customPath, setCustomPath] = useState('/sports');
  const [sport, setSport] = useState('basketball');
  const [league, setLeague] = useState('');
  const [market, setMarket] = useState('moneyline');
  const [sportsbooks, setSportsbooks] = useState('bet365,draftkings,fanduel');
  const [fixtureId, setFixtureId] = useState('');
  const [date, setDate] = useState('');
  const [groupOdds, setGroupOdds] = useState(true);
  const [showRawOdds, setShowRawOdds] = useState(false);
  const [sweepOut, setSweepOut] = useState('');

  // no client key usage; all calls proxy through Functions

  const run = async (label, fn) => {
    setLoading(true);
    setOutput(`Running: ${label}...`);
    try {
      const r = await fn();
      setOutput(`${label} → ${r.status}${r.ok ? ' OK' : ' ERR'}\n` + JSON.stringify(r.body, null, 2));
    } catch (e) {
      setOutput(`${label} → Failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const useProxy = useMemo(() => {
    const base = (
      (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
      || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
    );
    return Platform.OS === 'web' && !!base;
  }, []);

  const toQS = (obj) => {
    const params = Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');
    if (!params.length) return '';
    const s = params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    return s;
  };

  const loadFirstActiveFixture = async () => {
    await run('GET /fixtures/active (first fixture)', async () => {
      const resp = await runOpticOddsRequest(`/fixtures/active?${toQS({ sport, league, date })}`);
      try {
        const data = resp?.body?.data || resp?.body || [];
        const first = Array.isArray(data) ? data[0] : (Array.isArray(data?.items) ? data.items[0] : null);
        const id = first?.fixture_id || first?.id || first?.fixtureId || first?.game_id || '';
        if (id) setFixtureId(String(id));
      } catch {}
      return resp;
    });
  };

  const openDoc = (url) => {
    try { if (Platform.OS === 'web') window.open(url, '_blank'); } catch {}
  };

  const groupOddsPayload = (payload) => {
    const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []));
    const grouped = {};
    for (const o of list) {
      const sportsbook = o?.sportsbook || o?.book || 'unknown';
      const m = o?.market || o?.market_name || 'unknown';
      const gk = o?.grouping_key || `${o?.selection || o?.name || ''}:${o?.selection_line ?? o?.line ?? ''}`;
      if (!grouped[sportsbook]) grouped[sportsbook] = {};
      if (!grouped[sportsbook][m]) grouped[sportsbook][m] = {};
      if (!grouped[sportsbook][m][gk]) grouped[sportsbook][m][gk] = [];
      grouped[sportsbook][m][gk].push({
        id: o?.id,
        fixture_id: o?.fixture_id || o?.game_id,
        selection: o?.selection || o?.name,
        selection_line: o?.selection_line ?? o?.line,
        price: o?.price ?? o?.odds ?? o?.american_price,
        is_main: o?.is_main === true,
      });
    }
    return grouped;
  };

  const runOdds = async (label, pathBuilder) => {
    await run(label, async () => {
      const resp = await runOpticOddsRequest(pathBuilder());
      if (groupOdds) {
        try {
          const grouped = groupOddsPayload(resp.body);
          const payload = showRawOdds ? { grouped, raw: resp.body } : { grouped };
          return { status: resp.status, ok: resp.ok, body: payload };
        } catch {
          // fall back to raw
        }
      }
      return resp;
    });
  };

  const runEndpointSweep = async () => {
    setLoading(true);
    const base = (
      (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
      || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
      || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
    );
    
    const testEndpoint = async (name, path, method = 'GET', body = null) => {
      try {
        const opts = {
          method,
          headers: { 'Content-Type': 'application/json' }
        };
        if (body) opts.body = JSON.stringify(body);
        
        const fullUrl = `${base}${path}`;
        const res = await fetch(fullUrl, opts);
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = text; }
        
        return {
          name,
          path,
          fullUrl,
          method,
          status: res.status,
          ok: res.ok,
          statusText: res.statusText,
          data,
          hasData: !!data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)
        };
    } catch (e) {
        return {
          name,
          path,
          fullUrl: `${base}${path}`,
          method,
          status: 0,
          ok: false,
          statusText: e.message,
          hasData: false,
          error: true
        };
      }
    };
    
    setSweepOut('🔄 Testing endpoints...\n\n');
    
    const today = new Date().toISOString().slice(0,10);
    
    // First, fetch active fixtures and real odds for parlay test
    setSweepOut(prev => prev + '📋 Fetching fixtures and odds for parlay/SGP test...\n');
    setSweepOut(prev => prev + '   ℹ️ Testing Same Game Parlay (2+ legs from one game)\n\n');
    let validParlayData = null;
    
    // Try multiple sports/leagues to find live odds
    const leaguesToTry = [
      { sport: 'basketball', league: 'nba', name: 'NBA' },
      { sport: 'american_football', league: 'nfl', name: 'NFL' },
      { sport: 'ice_hockey', league: 'nhl', name: 'NHL' },
      { sport: 'basketball', league: 'ncaab', name: 'NCAAB' }
    ];
    
    try {
      for (const leagueConfig of leaguesToTry) {
        if (validParlayData) break; // Stop if we found valid data
        
        setSweepOut(prev => prev + `   🏀 Trying ${leagueConfig.name}...\n`);
        
        // Fetch active fixtures with odds - get up to 100 fixtures
        const fixturesResult = await testEndpoint(
          'Fetching Fixtures',
          `/opticOddsProxy?path=/fixtures/active?sport=${leagueConfig.sport}&league=${leagueConfig.league}&limit=100`,
          'GET'
        );
        
        if (fixturesResult.ok && fixturesResult.data?.data && Array.isArray(fixturesResult.data.data) && fixturesResult.data.data.length > 0) {
          const fixtures = fixturesResult.data.data;
          setSweepOut(prev => prev + `      Found ${fixtures.length} ${leagueConfig.name} fixtures\n`);
          
          // Try to find ONE fixture with 2+ different markets for SGP
          let oddsFoundCount = 0;
          let fixturesChecked = 0;
          
          for (let i = 0; i < fixtures.length; i++) {
            const fixture = fixtures[i];
            const testFixtureId = fixture.id || fixture.fixture_id;
            
            if (!testFixtureId) continue;
            
            fixturesChecked++;
            
            // Show ALL fixtures
            setSweepOut(prev => prev + `      🔍 [${i + 1}/${fixtures.length}] ${fixture.home_team || fixture.home_team_display || 'Home'} vs ${fixture.away_team || fixture.away_team_display || 'Away'}...\n`);
            
            // Fetch ALL odds for this fixture (not just one market)
            // OpticOdds requires at least one sportsbook parameter
            const oddsResult = await testEndpoint(
              'Fetching Odds',
              `/opticOddsProxy?path=/fixtures/odds?fixture_id=${testFixtureId}&sportsbook=draftkings`,
              'GET'
            );
            
            // Check if we got valid odds back
            if (oddsResult.ok && oddsResult.data?.data) {
              const fixtureData = Array.isArray(oddsResult.data.data) ? oddsResult.data.data[0] : oddsResult.data.data;
              const oddsArray = fixtureData?.odds || [];
              
              if (Array.isArray(oddsArray) && oddsArray.length > 0) {
                oddsFoundCount++;
                setSweepOut(prev => prev + `         ✅ ${oddsArray.length} odds available\n`);
                
                // Find 2-3 different markets from THIS fixture for SGP
                const parlayFriendlyMarkets = ['moneyline', 'spread', 'totals', 'total', 'point spread', 'money line', 'player points', 'player rebounds', 'player assists'];
                const suitableOdds = oddsArray.filter(odd => 
                  odd.market && 
                  parlayFriendlyMarkets.some(market => odd.market.toLowerCase().includes(market)) &&
                  odd.selection && 
                  odd.sportsbook && 
                  typeof odd.price === 'number'
                );
                
                // Get 2-3 different markets (not just same market different teams)
                const uniqueMarkets = new Map();
                for (const odd of suitableOdds) {
                  if (uniqueMarkets.size < 3 && !uniqueMarkets.has(odd.market)) {
                    uniqueMarkets.set(odd.market, odd);
                  }
                }
                
                const parlayLegs = Array.from(uniqueMarkets.values());
                
                if (parlayLegs.length >= 2) {
                  setSweepOut(prev => prev + `         📊 Found ${parlayLegs.length} different markets:\n`);
                  parlayLegs.forEach(leg => {
                    setSweepOut(prev => prev + `            • ${leg.market}: ${leg.selection}\n`);
                  });
                  
                  // Test SGP with these legs
                  setSweepOut(prev => prev + `         🧪 Testing SGP...\n`);
                  
                  const testParlayData = {
                    sport: leagueConfig.sport,
                    league: leagueConfig.league,
                    sportsbooks: [parlayLegs[0].sportsbook],
                    entries: parlayLegs.map(leg => ({
                      fixture_id: testFixtureId,
                      market: leg.market,
                      selection: leg.selection,
                      name: leg.name || leg.selection
                    }))
                  };
                  
                  const parlayTestResult = await testEndpoint(
                    'Testing Parlay',
                    '/opticOddsProxy',
                    'POST',
                    { path: '/parlay/odds', ...testParlayData }
                  );
                  
                  if (parlayTestResult.ok && parlayTestResult.data?.data) {
                    validParlayData = testParlayData;
                    
                    // Display SGP pricing from response
                    const sgpData = parlayTestResult.data.data;
                    const sportsbook = testParlayData.sportsbooks[0];
                    const bookData = sgpData[sportsbook];
                    
                    if (bookData && bookData.price) {
                      setSweepOut(prev => prev + `         ✅ SGP test PASSED! ${parlayLegs.length}-leg parlay on ${sportsbook}:\n`);
                      setSweepOut(prev => prev + `            💰 Price: ${bookData.price > 0 ? '+' : ''}${bookData.price}\n`);
                      setSweepOut(prev => prev + `            📊 Legs:\n`);
                      bookData.legs?.forEach(leg => {
                        setSweepOut(prev => prev + `               • ${leg.market}: ${leg.name} (${leg.price > 0 ? '+' : ''}${leg.price})\n`);
                      });
                      setSweepOut(prev => prev + `\n`);
                    } else {
                      setSweepOut(prev => prev + `         ✅ SGP test PASSED! Found valid ${parlayLegs.length}-leg Same Game Parlay.\n\n`);
                    }
                    break;
                  } else {
                    setSweepOut(prev => prev + `         ⚠️  SGP test failed, trying next fixture...\n`);
                  }
                } else {
                  setSweepOut(prev => prev + `         ⏭️ Only ${parlayLegs.length} market(s), need 2+ for SGP\n`);
                }
              } else {
                setSweepOut(prev => prev + `         ⏭️ No odds\n`);
              }
            } else {
              // Show detailed error on first 3 failures
              if (i < 3) {
                setSweepOut(prev => prev + `         ❌ Failed: ${oddsResult.status} ${oddsResult.statusText || ''}\n`);
                if (oddsResult.data?.error) {
                  setSweepOut(prev => prev + `            Error: ${oddsResult.data.error}\n`);
                }
              } else {
                setSweepOut(prev => prev + `         ❌ Failed to fetch\n`);
              }
            }
          }
          
          // Summary for this league
          setSweepOut(prev => prev + `      📈 Summary: Checked ${fixturesChecked} fixtures, ${oddsFoundCount} had odds data\n`);
          
          if (!validParlayData) {
            setSweepOut(prev => prev + `      ⏭️  No live odds found in ${fixtures.length} ${leagueConfig.name} fixtures\n\n`);
          }
        } else {
          setSweepOut(prev => prev + `      ⚠️  No ${leagueConfig.name} fixtures available\n\n`);
        }
      }
      
      // If no valid data found, mark as expected failure
      if (!validParlayData) {
        setSweepOut(prev => prev + `   ⚠️  Could not find fixture with 2+ markets for SGP - test will be skipped\n\n`);
        validParlayData = {
          sport: 'basketball',
          league: 'nba',
          sportsbooks: [],
          entries: [],
          skipTest: true
        };
      }
    } catch (e) {
      setSweepOut(prev => prev + `   ⚠️  Error: ${e.message}\n\n`);
      validParlayData = {
        sport: 'basketball',
        league: 'nba',
        sportsbooks: [],
        entries: [],
        skipTest: true
      };
    }
    
    const endpoints = [
      // === OpticOdds Direct API Endpoints ===
      { name: 'OpticOdds: Get Sports', path: '/opticOddsProxy?path=/sports', method: 'GET' },
      { name: 'OpticOdds: Get Sportsbooks', path: '/opticOddsProxy?path=/sportsbooks', method: 'GET' },
      { name: 'OpticOdds: Active Fixtures', path: `/opticOddsProxy?path=/fixtures/active?sport=basketball&league=nba&date=${today}`, method: 'GET' },
      { name: 'OpticOdds: Markets', path: '/opticOddsProxy?path=/markets?sport=basketball', method: 'GET' },
      { 
        name: validParlayData?.skipTest ? 'OpticOdds: SGP/Parlay Odds (No Live Data)' : 'OpticOdds: SGP/Parlay Odds', 
        path: '/opticOddsProxy', 
        method: 'POST', 
        body: { 
          path: '/parlay/odds',
          ...validParlayData
        },
        expectedFail: validParlayData?.skipTest // Only mark as expected fail if we couldn't find live data
      },
      { name: 'OpticOdds: Copilot Odds (Premium)', path: `/opticOddsProxy?path=/copilot/fixtures/odds?sport=basketball&league=nba&date=${today}`, method: 'GET', expectedFail: true },
      
      // === Firebase Functions Endpoints ===
      { name: 'Function: Natural Language Odds', path: '/nlOdds', method: 'POST', body: { prompt: 'Lakers ML tonight' } },
      { name: 'Function: NL SGP Query', path: '/nlOdds', method: 'POST', body: { prompt: 'Lakers ML + LeBron 25+ pts SGP tonight' } },
      { name: 'Function: Endpoint Sweep', path: '/opticOddsSweep', method: 'POST', body: { sport: 'basketball', league: 'nba', date: today } },
      
      // === Cache System ===
      { name: 'Cache: Get Stats', path: '/getCacheStats', method: 'GET' },
      { name: 'Cache: Get Logs', path: '/getCacheLogs', method: 'GET' },
      { name: 'Cache: All Fixtures', path: '/getAllCachedFixtures?limit=10', method: 'GET' },
      
      // === User Management ===
      { name: 'User: Check Query Limit', path: '/checkAndIncrementQuery', method: 'POST', body: { userId: 'test' } },
      { name: 'User: Get Usage', path: '/getQueryUsage?userId=test', method: 'GET' },
    ];
    
    let output = '';
    const results = [];
    
    for (let i = 0; i < endpoints.length; i++) {
      const ep = endpoints[i];
      setSweepOut(prev => prev + `\n⏳ ${ep.name}...`);
      
      const result = await testEndpoint(ep.name, ep.path, ep.method, ep.body);
      results.push({ ...result, expectedFail: ep.expectedFail });
      
      const icon = result.ok ? '✅' : (ep.expectedFail ? '⚠️' : '❌');
      const statusColor = result.ok ? '🟢' : (ep.expectedFail ? '🟡' : '🔴');
      const note = ep.expectedFail && !result.ok ? ' (Expected - requires valid data/premium access)' : '';
      
      output += `${icon} ${ep.name}\n`;
      output += `   ${statusColor} ${result.status} ${result.statusText}${note}\n`;
      output += `   🌐 ${result.fullUrl}\n\n`;
    }
    
    // Summary
    const passed = results.filter(r => r.ok).length;
    const failedUnexpected = results.filter(r => !r.ok && !r.expectedFail).length;
    const failedExpected = results.filter(r => !r.ok && r.expectedFail).length;
    const total = results.length;
    
    output += `\n${'='.repeat(60)}\n`;
    output += `📊 ENDPOINT HEALTH CHECK SUMMARY\n`;
    output += `${'='.repeat(60)}\n`;
    output += `✅ Passed: ${passed}/${total} endpoints\n`;
    output += `❌ Failed: ${failedUnexpected}/${total} endpoints\n`;
    if (failedExpected > 0) {
      output += `⚠️  Expected Failures: ${failedExpected}/${total} endpoints (require valid data/access)\n`;
    }
    output += `📈 Success Rate: ${Math.round((passed/total) * 100)}%\n`;
    output += `\n${failedUnexpected === 0 ? '🎉 All critical systems operational!' : '⚠️  Some critical endpoints need attention'}\n`;
    
    setSweepOut(output);
    setLoading(false);
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>OpticOdds API Tests</Text>
      <Text style={styles.caption}>Mode: {useProxy ? 'Proxy via Functions' : 'Direct (disabled)'}</Text>

      <View style={styles.row}>
        <Text style={styles.label}>Custom Path</Text>
        <TextInput
          style={styles.input}
          value={customPath}
          onChangeText={setCustomPath}
          placeholder="/sports or /leagues?sport=basketball"
          placeholderTextColor="rgba(255,255,255,0.5)"
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run(`GET ${customPath}`, () => runOpticOddsRequest(customPath))}>
          <Text style={styles.btnText}>Run</Text>
        </TouchableOpacity>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={styles.groupHeader}>Reference</Text>
        <TouchableOpacity onPress={() => openDoc('https://developer.opticodds.com/reference/getting-started')}><Text style={styles.link}>Docs</Text></TouchableOpacity>
      </View>
      <View style={[styles.row, { flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /sports', () => runOpticOddsRequest('/sports'))}>
          <Text style={styles.btnText}>GET /sports</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /leagues?sport=basketball', () => runOpticOddsRequest('/leagues?sport=basketball'))}>
          <Text style={styles.btnText}>GET /leagues?basketball</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /fixtures/active?sport=basketball', () => runOpticOddsRequest('/fixtures/active?sport=basketball'))}>
          <Text style={styles.btnText}>GET /fixtures/active</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /sportsbooks', () => runOpticOddsRequest('/sportsbooks'))}>
          <Text style={styles.btnText}>GET /sportsbooks</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /markets?sport=basketball', () => runOpticOddsRequest('/markets?sport=basketball'))}>
          <Text style={styles.btnText}>GET /markets?basketball</Text>
        </TouchableOpacity>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 24 }}>
        <Text style={styles.groupHeader}>🏥 Site Health Check</Text>
      </View>
      <View style={[styles.row, { flexWrap: 'wrap', gap: 8 }]}> 
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={runEndpointSweep}>
          <Text style={styles.btnText}>{loading ? '⏳ Running…' : '🚀 Run Health Check'}</Text>
        </TouchableOpacity>
      </View>
      {sweepOut ? (
        <View style={{
          backgroundColor: '#1a1a1a',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#333',
          padding: 16,
          marginTop: 12,
          marginBottom: 24,
          maxHeight: 500,
          overflow: 'hidden'
        }}>
          <ScrollView style={{ maxHeight: 480 }}>
            <Text style={[styles.outputText, { fontSize: 13, lineHeight: 20 }]}>{sweepOut}</Text>
          </ScrollView>
        </View>
      ) : null}

      <Text style={styles.groupHeader}>Params</Text>
      <View style={styles.row}>
        <Text style={styles.label}>Sport</Text>
        <TextInput style={styles.input} value={sport} onChangeText={setSport} placeholder="basketball" placeholderTextColor="rgba(255,255,255,0.5)" />
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>League</Text>
        <TextInput style={styles.input} value={league} onChangeText={setLeague} placeholder="nba" placeholderTextColor="rgba(255,255,255,0.5)" />
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>Date</Text>
        <TextInput style={styles.input} value={date} onChangeText={setDate} placeholder="YYYY-MM-DD (optional)" placeholderTextColor="rgba(255,255,255,0.5)" />
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>Market</Text>
        <TextInput style={styles.input} value={market} onChangeText={setMarket} placeholder="moneyline" placeholderTextColor="rgba(255,255,255,0.5)" />
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>Sportsbooks</Text>
        <TextInput style={styles.input} value={sportsbooks} onChangeText={setSportsbooks} placeholder="comma,separated" placeholderTextColor="rgba(255,255,255,0.5)" />
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>Fixture ID</Text>
        <TextInput style={styles.input} value={fixtureId} onChangeText={setFixtureId} placeholder="from fixtures" placeholderTextColor="rgba(255,255,255,0.5)" />
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={loadFirstActiveFixture}>
          <Text style={styles.btnText}>Load First Active</Text>
        </TouchableOpacity>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={styles.groupHeader}>Fixtures</Text>
        <TouchableOpacity onPress={() => openDoc('https://developer.opticodds.com/reference/get_fixtures-active')}><Text style={styles.link}>Docs</Text></TouchableOpacity>
      </View>
      <View style={[styles.row, { flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('GET /fixtures/active (params)', () => runOpticOddsRequest(`/fixtures/active?${toQS({ sport, league, date })}`))}>
          <Text style={styles.btnText}>GET /fixtures/active (params)</Text>
        </TouchableOpacity>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={styles.groupHeader}>Odds</Text>
        <TouchableOpacity onPress={() => openDoc('https://developer.opticodds.com/reference/get_fixtures-odds')}><Text style={styles.link}>Docs</Text></TouchableOpacity>
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>Group Odds</Text>
        <Switch value={groupOdds} onValueChange={setGroupOdds} />
        <Text style={styles.hintText}>{groupOdds ? 'grouping_key' : 'raw list'}</Text>
        <View style={{ width: 10 }} />
        <Text style={styles.label}>Show Raw</Text>
        <Switch value={showRawOdds} onValueChange={setShowRawOdds} />
      </View>
      <View style={[styles.row, { flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity style={styles.btn} disabled={loading || !fixtureId} onPress={() => runOdds('GET /fixtures/odds (by fixture)', () => `/fixtures/odds?${toQS({ fixture_id: fixtureId, market, sportsbooks })}`)}>
          <Text style={styles.btnText}>Odds: Fixture</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => runOdds('GET /fixtures/odds (league/day)', () => `/fixtures/odds?${toQS({ sport, league, market, sportsbooks, date })}`)}>
          <Text style={styles.btnText}>Odds: League/Date</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.groupHeader}>Quick Odds Flow</Text>
      <View style={[styles.row, { flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => run('1) Get active fixtures', () => runOpticOddsRequest(`/fixtures/active?${toQS({ sport, league, date })}`))}>
          <Text style={styles.btnText}>1) Fixtures</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading} onPress={loadFirstActiveFixture}>
          <Text style={styles.btnText}>2) Use First Fixture</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.btn} disabled={loading || !fixtureId} onPress={() => runOdds('3) Fixture Odds (grouped)', () => `/fixtures/odds?${toQS({ fixture_id: fixtureId, market, sportsbooks })}`)}>
          <Text style={styles.btnText}>3) Odds</Text>
        </TouchableOpacity>
      </View>

      

      <ScrollView style={styles.outputBox}>
        <Text style={styles.outputText}>{output}</Text>
      </ScrollView>
    </View>
  );
}

function AiTab() {
  const [prompt, setPrompt] = useState('');
  const [sport, setSport] = useState('');
  const [league, setLeague] = useState('');
  const [date, setDate] = useState('');
  const [sportsbooks, setSportsbooks] = useState('');
  const [booksList, setBooksList] = useState([]);
  const [booksOpen, setBooksOpen] = useState(false);
  const [market, setMarket] = useState('');
  const [marketsList, setMarketsList] = useState([]);
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [friendly, setFriendly] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [queries, setQueries] = useState([]);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await runOpticOddsRequest('/sportsbooks');
        const arr = Array.isArray(r?.body?.data) ? r.body.data : (Array.isArray(r?.body) ? r.body : (Array.isArray(r?.body?.items) ? r.body.items : []));
        const names = arr.map((b) => b?.name || b).filter(Boolean).sort();
        setBooksList(names);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const qSport = sport || 'basketball';
        const r = await runOpticOddsRequest(`/markets?sport=${encodeURIComponent(qSport)}`);
        const arr = Array.isArray(r?.body?.data) ? r.body.data : (Array.isArray(r?.body) ? r.body : (Array.isArray(r?.body?.items) ? r.body.items : []));
        const names = arr.map((m) => m?.name || m?.market || m).filter(Boolean);
        const dedup = Array.from(new Set(names)).sort();
        setMarketsList(dedup);
      } catch {}
    })();
  }, [sport]);

  const run = async () => {
    setLoading(true);
    setHasRun(true);
    setOutput('Mapping intent and fetching odds...');
    try {
      const effectiveDate = (date && date.trim()) ? date.trim() : undefined;
      // Show immediate placeholder in the preview while mapping/fetching
      setFriendly([]);
      setEndpoints([]);
      setQueries([{
        selection: 'Parsing…',
        selection_line: null,
        market: market || '—',
        date: effectiveDate || '',
        sport: sport || '—',
        league: league || '—'
      }]);
      const defaults = {
        sport: sport || undefined,
        league: league || undefined,
        date: effectiveDate,
        sportsbooks: sportsbooks || undefined,
        market: market || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        now: new Date().toISOString()
      };
      const r = await runNlOdds(prompt, defaults);
      setEndpoints(r?.body?.endpoints || []);
      const qList = Array.isArray(r?.body?.mapping?.queries) ? r.body.mapping.queries : (Array.isArray(r?.body?.results) ? r.body.results.map(x => x?.query).filter(Boolean) : []);
      setQueries(qList);
      const results = Array.isArray(r?.body?.results) ? r.body.results : [];
      const rendered = [];
      const toText = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') {
          if (Array.isArray(v)) return v.map(toText).filter(Boolean).join(', ');
          return v.name || v.label || v.title || v.display || v.id || '';
        }
        return String(v);
      };
      const BOOK_LINKS = {
        DraftKings: 'https://sportsbook.draftkings.com/',
        FanDuel: 'https://sportsbook.fanduel.com/',
        Caesars: 'https://www.caesars.com/sportsbook',
        bet365: 'https://www.bet365.com/',
        BetMGM: 'https://sports.betmgm.com/en/sports',
        PointsBet: 'https://pointsbet.com/'
      };
      for (const res of results) {
        const rawItems = Array.isArray(res?.items) ? res.items : [];
        const items = rawItems.flatMap((it) => Array.isArray(it?.odds) ? it.odds : [it]);
        for (const o of items) {
          const line = toText(o.selection_line ?? o.line ?? o.points ?? '');
          const sel = toText(o.selection || o.name || o.team_name || o.player_name || '');
          const bookName = toText(o.sportsbook || o.book || '');
          const priceRaw = o.price ?? o.american_price ?? o.odds;
          const price = (typeof priceRaw === 'number' || typeof priceRaw === 'string') ? String(priceRaw) : '';
          const market = toText(o.market || o.market_name || o.group || '');
          const fixture = toText(o.fixture_id || o.game_id || '');
          const deep = (o?.urls && (o.urls?.offer || o.urls?.deeplink || o.urls?.event || o.urls?.market)) || BOOK_LINKS[bookName] || null;
          if (!market && !sel) continue;
          rendered.push({
            sportsbook: bookName,
            market,
            selection: sel,
            line,
            price,
            fixtureId: fixture,
            deepLink: deep,
            date: (res?.query?.date || '').toString(),
            sport: res?.query?.sport || '',
            league: res?.query?.league || ''
          });
        }
      }
      // Include Same Game Parlay (SGP) summary when present
      try {
        const sgp = r?.body?.sgp;
        const entries = Array.isArray(sgp?.request?.entries) ? sgp.request.entries : [];
        if (entries.length >= 2) {
          const legsCount = entries.length;
          const legsText = entries.map((e) => `${e.market}: ${e.name}`).join(' + ');
          const offered = typeof sgp?.math?.offered_price_american === 'number' ? sgp.math.offered_price_american : null;
          const ai = typeof sgp?.math?.ai_price_american === 'number' ? sgp.math.ai_price_american : null;
          const evp = typeof sgp?.math?.ev_percent_vs_ai === 'number' ? sgp.math.ev_percent_vs_ai : null;
          const bookList = Array.isArray(sgp?.request?.sportsbooks) ? sgp.request.sportsbooks : [];
          const book = offered !== null ? (bookList[0] || 'Sportsbook') : (bookList[0] || 'OpticOdds AI');
          rendered.unshift({
            sportsbook: book,
            market: `Same Game Parlay (${legsCount} legs)`,
            selection: legsText,
            line: '',
            price: offered ?? ai ?? null,
            fixtureId: sgp?.request?.fixture_id || '',
            deepLink: null,
            date: (Array.isArray(results) && results[0]?.query?.date) || (qList?.[0]?.date) || '',
            sport: (Array.isArray(results) && results[0]?.query?.sport) || (qList?.[0]?.sport) || '',
            league: (Array.isArray(results) && results[0]?.query?.league) || (qList?.[0]?.league) || '',
            // Attach computed EV% to help display in description formatting below
            evPercent: evp
          });
        }
      } catch {}
      setFriendly(rendered);
      setOutput(`NL → Odds ${r.status}${r.ok ? ' OK' : ' ERR'}\n` + JSON.stringify(r.body, null, 2));
    } catch (e) {
      setOutput(`Failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>bOPT AI (Natural Language → Odds)</Text>
      <Text style={styles.caption}>Describe the bet in plain English. We map it to OpticOdds and fetch odds.</Text>

      <View style={styles.card}>
        <View style={[styles.row, { marginBottom: 16 }]}>
          <Text style={styles.label}>Prompt</Text>
          <TextInput style={styles.input} value={prompt} onChangeText={setPrompt} placeholder="Team to win and player prop..." placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>

        <Text style={styles.groupHeader}>Defaults</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Sport</Text>
          <TextInput style={styles.input} value={sport} onChangeText={setSport} placeholder="basketball" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>League</Text>
          <TextInput style={styles.input} value={league} onChangeText={setLeague} placeholder="nba" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date</Text>
          <TextInput style={styles.input} value={date} onChangeText={setDate} placeholder="YYYY-MM-DD (defaults to today)" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={[styles.row, { alignItems: 'flex-start' }]}>
          <Text style={styles.label}>Market</Text>
          <View style={{ flex: 1 }}>
            <View style={[styles.input, { paddingVertical: 8 }]}> 
              <Text style={{ color: '#fff' }}>{market || 'Any (none selected)'}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              <TouchableOpacity style={styles.smallBtn} onPress={() => setMarketsOpen(v => !v)}>
                <Text style={styles.smallBtnText}>{marketsOpen ? 'Hide Markets' : 'Select Markets'}</Text>
              </TouchableOpacity>
            </View>
            {marketsOpen ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {marketsList.map((m) => {
                  const selected = market === m;
                  return (
                    <TouchableOpacity key={m} style={[styles.tag, selected ? styles.tagActive : null]} onPress={() => setMarket(selected ? '' : m)}>
                      <Text style={[styles.tagText, selected ? styles.tagTextActive : null]}>{m}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>

        <View style={[styles.row, { alignItems: 'flex-start' }]}>
          <Text style={styles.label}>Sportsbooks</Text>
          <View style={{ flex: 1 }}>
            <View style={[styles.input, { paddingVertical: 8 }]}> 
              <Text style={{ color: '#fff' }}>{sportsbooks || 'All (none selected)'}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              <TouchableOpacity style={styles.smallBtn} onPress={() => setBooksOpen(v => !v)}>
                <Text style={styles.smallBtnText}>{booksOpen ? 'Hide Sportsbooks' : 'Select Sportsbooks'}</Text>
              </TouchableOpacity>
            </View>
            {booksOpen ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {booksList.map((b) => {
                  const selected = (sportsbooks || '').split(',').filter(Boolean).includes(b);
                  return (
                    <TouchableOpacity key={b} style={[styles.tag, selected ? styles.tagActive : null]} onPress={() => {
                      const cur = (sportsbooks || '').split(',').filter(Boolean);
                      const idx = cur.indexOf(b);
                      if (idx >= 0) cur.splice(idx, 1); else cur.push(b);
                      setSportsbooks(cur.join(','));
                    }}>
                      <Text style={[styles.tagText, selected ? styles.tagTextActive : null]}>{b}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>

        <View style={[styles.row, { flexWrap: 'wrap', gap: 8, marginTop: 8 }]}>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={run}>
            <Text style={styles.btnText}>{loading ? 'Running…' : 'Run bOPT AI'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Find best parlays today with top books')}>
            <Text style={styles.btnText}>Try: Best Parlays Today</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Make me the best 30% NBA SGP on FanDuel tonight')}>
            <Text style={styles.btnText}>Try: Make me the best 30% NBA SGP on FanDuel tonight</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Lakers -3.5 and LeBron over 9.5 assists tomorrow')}>
            <Text style={styles.btnText}>Try: Lakers -3.5 + LeBron A</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Guardians moneyline and Ramirez to record a hit')}>
            <Text style={styles.btnText}>Try: Guardians ML + Ramirez hit</Text>
          </TouchableOpacity>
        </View>
      </View>

      {hasRun ? (
        <View style={[styles.card, { marginTop: 12 }]}> 
          <Text style={styles.groupHeader}>Available Bets</Text>
          {loading ? (
            <Text style={styles.caption}>Loading available bets…</Text>
          ) : friendly.length ? (
            <View style={{ gap: 12 }}>
              {friendly.map((f, idx) => (
                <LegBetCard
                  key={`${f.fixtureId || idx}-${idx}`}
                  option={{
                    title: `${f.market}`,
                    description: `${f.selection}${f.line ? ` ${f.line}` : ''} • ${f.sportsbook}${f.price ? ` • ${f.price}` : ''}`,
                    amount: f.date || ''
                  }}
                  index={idx}
                  isSelected={false}
                  userSelectedSportsbooks={[]}
                  onCardPress={() => {
                    if (f.deepLink && Platform.OS === 'web') {
                      try { window.open(f.deepLink, '_blank'); } catch {}
                    }
                  }}
                />
              ))}
            </View>
          ) : (
            <Text style={styles.caption}>No bets found. Try a different market, date, or sportsbooks.</Text>
          )}
        </View>
      ) : null}

      <ScrollView style={styles.outputBox}>
        <Text style={styles.outputText}>{output}</Text>
      </ScrollView>

      {hasRun ? (
        <View style={[styles.card, { marginTop: 12 }]}> 
          <Text style={styles.groupHeader}>Endpoints Called</Text>
          {endpoints.length ? endpoints.map((e) => (
            <Text key={e} style={styles.caption}>{e}</Text>
          )) : (
            <Text style={styles.caption}>No endpoints yet.</Text>
          )}
        </View>
      ) : null}
    </View>
  );
}

function MetricsTab() {
  const [metrics, setMetrics] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h'); // '24h' | '7d' | '30d' | 'all'
  const [filterNoResults, setFilterNoResults] = useState(false);
  const [filterNoOddsData, setFilterNoOddsData] = useState(false);
  const [expandedQueryId, setExpandedQueryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    const q = query(collection(db, 'metrics_queries'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        list.push({ id: d.id, ...data });
      });
      setMetrics(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Load user data for mapping UIDs to emails
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsub = onSnapshot(q, (snap) => {
      const userMap = {};
      snap.forEach((d) => {
        const data = d.data();
        userMap[d.id] = {
          email: data.email || d.id,
          displayName: data.displayName || null,
          userType: data.userType || 'user'
        };
      });
      setUsers(userMap);
    });
    return () => unsub();
  }, []);

  const filteredMetrics = useMemo(() => {
    let filtered = metrics;
    
    // Apply time range filter
    if (timeRange !== 'all') {
      const now = Date.now();
      const cutoff = {
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      }[timeRange];
      filtered = filtered.filter(m => {
        const ts = m.createdAt?.toMillis?.() || 0;
        return (now - ts) <= cutoff;
      });
    }
    
    // Apply no-results filter (complete failure - no fixtures found)
    if (filterNoResults) {
      filtered = filtered.filter(m => {
        const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
        const hasNoOddsMarker = m.logs?.some(log => 
          log.event === 'fixtures_found_no_odds' || 
          log.event?.includes('no_odds')
        );
        // True no results: 0 results AND no "fixtures found but no odds" marker
        return hasNoResults && !hasNoOddsMarker;
      });
    }
    
    // Apply no-odds-data filter (fixtures found but no odds posted)
    if (filterNoOddsData) {
      filtered = filtered.filter(m => {
        const hasNoOddsMarker = m.logs?.some(log => 
          log.event === 'fixtures_found_no_odds' || 
          log.event?.includes('no_odds') ||
          log.event?.includes('coming_soon')
        );
        return hasNoOddsMarker;
      });
    }
    
    return filtered;
  }, [metrics, timeRange, filterNoResults, filterNoOddsData]);

  const stats = useMemo(() => {
    if (!filteredMetrics.length) return null;
    const total = filteredMetrics.length;
    const positive = filteredMetrics.filter(m => m.feedback === 'up').length;
    const negative = filteredMetrics.filter(m => m.feedback === 'down').length;
    const avgDuration = Math.round(filteredMetrics.reduce((sum, m) => sum + (m.durationMs || 0), 0) / total);
    const avgResults = Math.round(filteredMetrics.reduce((sum, m) => sum + (m.resultsCount || 0), 0) / total);
    const broadenCount = filteredMetrics.filter(m => m.broaden).length;
    
    // Distinguish between true no results and no odds data
    const trueNoResultsCount = filteredMetrics.filter(m => {
      const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
      const hasNoOddsMarker = m.logs?.some(log => 
        log.event === 'fixtures_found_no_odds' || 
        log.event?.includes('no_odds') ||
        log.event?.includes('coming_soon')
      );
      return hasNoResults && !hasNoOddsMarker;
    }).length;
    
    const noOddsDataCount = filteredMetrics.filter(m => {
      const hasNoOddsMarker = m.logs?.some(log => 
        log.event === 'fixtures_found_no_odds' || 
        log.event?.includes('no_odds') ||
        log.event?.includes('coming_soon')
      );
      return hasNoOddsMarker;
    }).length;
    
    // Watch/notification count - queries that resulted in user watching fixtures
    const watchCount = filteredMetrics.filter(m => {
      return m.logs?.some(log => 
        log.event === 'fixture_watched' || 
        log.event?.includes('watch')
      );
    }).length;
    
    // User stats
    const uniqueUsers = new Set(filteredMetrics.map(m => m.uid || 'anon')).size;
    const topUsers = Object.entries(
      filteredMetrics.reduce((acc, m) => {
        const uid = m.uid || 'anon';
        acc[uid] = (acc[uid] || 0) + 1;
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([uid, count]) => ({
        uid,
        count,
        email: users[uid]?.email || (uid === 'anon' ? 'Anonymous' : uid)
      }));
    
    return { 
      total, 
      positive, 
      negative, 
      avgDuration, 
      avgResults, 
      broadenCount, 
      trueNoResultsCount, 
      noOddsDataCount, 
      watchCount,
      uniqueUsers, 
      topUsers 
    };
  }, [filteredMetrics, users]);

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Query Metrics {loading ? '(loading...)' : `(${metrics.length} total)`}</Text>
      
      <View style={[styles.row, { marginBottom: 16 }]}>
        <Text style={styles.label}>Time Range:</Text>
        {['24h', '7d', '30d', 'all'].map((range) => (
          <TouchableOpacity
            key={range}
            onPress={() => setTimeRange(range)}
            style={[styles.tag, timeRange === range ? styles.tagActive : null]}
          >
            <Text style={[styles.tagText, timeRange === range ? styles.tagTextActive : null]}>
              {range === '24h' ? 'Last 24h' : range === '7d' ? 'Last 7d' : range === '30d' ? 'Last 30d' : 'All Time'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      
      <View style={[styles.row, { marginBottom: 16, flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity
          onPress={() => {
            setFilterNoResults(!filterNoResults);
            if (!filterNoResults) setFilterNoOddsData(false);
          }}
          style={[styles.tag, filterNoResults ? styles.tagActive : null]}
        >
          <Text style={[styles.tagText, filterNoResults ? styles.tagTextActive : null]}>
            {filterNoResults ? '✓ ' : ''}True No Results {stats?.trueNoResultsCount ? `(${stats.trueNoResultsCount})` : ''}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            setFilterNoOddsData(!filterNoOddsData);
            if (!filterNoOddsData) setFilterNoResults(false);
          }}
          style={[styles.tag, filterNoOddsData ? styles.tagActive : null]}
        >
          <Text style={[styles.tagText, filterNoOddsData ? styles.tagTextActive : null]}>
            {filterNoOddsData ? '✓ ' : ''}No Odds Data {stats?.noOddsDataCount ? `(${stats.noOddsDataCount})` : ''}
          </Text>
        </TouchableOpacity>
      </View>

      {stats ? (
        <View style={[styles.card, { marginBottom: 16 }]}>
          <Text style={styles.groupHeader}>Statistics</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Total Queries</Text>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.total}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Positive Feedback</Text>
              <Text style={{ color: '#4ade80', fontSize: 24, fontWeight: '700' }}>
                {stats.positive} <Text style={{ fontSize: 14 }}>👍</Text>
              </Text>
              <Text style={styles.caption}>{((stats.positive / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Negative Feedback</Text>
              <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>
                {stats.negative} <Text style={{ fontSize: 14 }}>👎</Text>
              </Text>
              <Text style={styles.caption}>{((stats.negative / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Avg Duration</Text>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.avgDuration}ms</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Avg Results</Text>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.avgResults}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Broadened Queries</Text>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.broadenCount}</Text>
              <Text style={styles.caption}>{((stats.broadenCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Unique Users</Text>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.uniqueUsers}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>True No Results</Text>
              <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>
                {stats.trueNoResultsCount} <Text style={{ fontSize: 14 }}>⚠️</Text>
              </Text>
              <Text style={styles.caption}>{((stats.trueNoResultsCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>No Odds Data</Text>
              <Text style={{ color: '#fbbf24', fontSize: 24, fontWeight: '700' }}>
                {stats.noOddsDataCount} <Text style={{ fontSize: 14 }}>🕐</Text>
              </Text>
              <Text style={styles.caption}>{((stats.noOddsDataCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Watched Fixtures</Text>
              <Text style={{ color: '#4CAF50', fontSize: 24, fontWeight: '700' }}>
                {stats.watchCount} <Text style={{ fontSize: 14 }}>👁️</Text>
              </Text>
              <Text style={styles.caption}>{((stats.watchCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
          </View>
          
          {/* Top Users */}
          {stats.topUsers.length > 0 && (
            <View style={{ marginTop: 16 }}>
              <Text style={[styles.groupHeader, { marginTop: 0 }]}>Top Users</Text>
              {stats.topUsers.map((user, idx) => (
                <View key={user.uid} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, width: 20 }}>#{idx + 1}</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, flex: 1 }}>{user.email}</Text>
                  <Text style={{ color: '#4ade80', fontSize: 14, fontWeight: '700' }}>{user.count} queries</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      ) : null}

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text style={styles.groupHeader}>Recent Queries</Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredMetrics.length)}-{Math.min(currentPage * itemsPerPage, filteredMetrics.length)} of {filteredMetrics.length}
        </Text>
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {filteredMetrics.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((m) => {
          const uid = m.uid || 'anon';
          const userInfo = users[uid];
          const userDisplay = userInfo ? (userInfo.email) : (uid === 'anon' ? 'Anonymous' : uid);
          const isAdmin = userInfo?.userType === 'admin';
          const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
          const isExpanded = expandedQueryId === m.id;
          
          return (
            <View 
              key={m.id} 
              style={[
                styles.userRow, 
                { flexDirection: 'column', alignItems: 'stretch' },
                hasNoResults && { borderColor: 'rgba(248, 113, 113, 0.3)', backgroundColor: 'rgba(248, 113, 113, 0.05)' }
              ]}
            >
              <TouchableOpacity 
                onPress={() => setExpandedQueryId(isExpanded ? null : m.id)}
                style={{ flex: 1 }}
                activeOpacity={0.7}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                      <Text style={styles.userEmail}>{m.prompt || 'No prompt'}</Text>
                      {hasNoResults && <Text style={{ fontSize: 16 }}>⚠️</Text>}
                      {m.logs && Array.isArray(m.logs) && m.logs.length > 0 && (
                        <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: 'rgba(78, 205, 196, 0.2)', borderRadius: 4 }}>
                          <Text style={{ color: '#4ecdc4', fontSize: 10, fontWeight: '700' }}>📋 {m.logs.length} LOGS</Text>
                        </View>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 2 }}>
                      <Text style={[styles.userMeta, { fontSize: 12 }]}>
                        👤 {userDisplay}
                      </Text>
                      {isAdmin && (
                        <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: 'rgba(139, 92, 246, 0.2)', borderRadius: 4 }}>
                          <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: '700' }}>ADMIN</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    {m.feedback === 'up' && <Text style={{ fontSize: 18 }}>👍</Text>}
                    {m.feedback === 'down' && <Text style={{ fontSize: 18 }}>👎</Text>}
                    {!m.feedback && <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>no feedback</Text>}
                    <View style={{ backgroundColor: isExpanded ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255,255,255,0.1)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 }}>
                      <Text style={{ color: isExpanded ? '#4ecdc4' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '700' }}>
                        {isExpanded ? '▲ COLLAPSE' : '▼ EXPAND'}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
                  <Text style={styles.userMeta}>Duration: {m.durationMs || 0}ms</Text>
                  <Text style={[styles.userMeta, hasNoResults && { color: '#f87171', fontWeight: '700' }]}>
                    Results: {m.resultsCount || 0}
                  </Text>
                  {m.broaden && <Text style={[styles.userMeta, { color: '#fbbf24' }]}>Broadened</Text>}
                  <Text style={styles.userMeta}>
                    {m.createdAt?.toDate?.() ? m.createdAt.toDate().toLocaleString() : 'Unknown date'}
                  </Text>
                </View>
              </TouchableOpacity>
              
              {/* Expanded Logs Section */}
              {isExpanded && m.logs && Array.isArray(m.logs) && m.logs.length > 0 && (
                <View style={{ marginTop: 12, padding: 12, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' }}>
                  <Text style={{ color: '#fff', fontWeight: '700', marginBottom: 8 }}>Search Logs:</Text>
                  <ScrollView style={{ maxHeight: 300 }}>
                    {m.logs.map((log, idx) => (
                      <View key={idx} style={{ marginBottom: 8, paddingBottom: 8, borderBottomWidth: idx < m.logs.length - 1 ? 1 : 0, borderBottomColor: 'rgba(255,255,255,0.05)' }}>
                        <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                          <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}
                          </Text>
                          <Text style={{ color: '#4ade80', fontSize: 11, fontWeight: '700', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                            {log.event}
                          </Text>
                        </View>
                        {log.error && (
                          <Text style={{ color: '#f87171', fontSize: 11, marginTop: 4, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                            Error: {log.error}
                          </Text>
                        )}
                        {log.resultsCount !== undefined && (
                          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
                            Results: {log.resultsCount}
                          </Text>
                        )}
                        {log.itemsCount !== undefined && (
                          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
                            Items: {log.itemsCount}
                          </Text>
                        )}
                        {log.finalResultsCount !== undefined && (
                          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
                            Final Results: {log.finalResultsCount}
                          </Text>
                        )}
                        {log.endpoint && (
                          <Text style={{ color: '#9fd3ff', fontSize: 11, marginTop: 2, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                            {log.endpoint}
                          </Text>
                        )}
                        {log.endpoints && Array.isArray(log.endpoints) && log.endpoints.length > 0 && (
                          <View style={{ marginTop: 4 }}>
                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>Endpoints called:</Text>
                            {log.endpoints.slice(0, 3).map((ep, i) => (
                              <Text key={i} style={{ color: '#9fd3ff', fontSize: 10, marginLeft: 8, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                                • {ep}
                              </Text>
                            ))}
                            {log.endpoints.length > 3 && (
                              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginLeft: 8 }}>
                                ...and {log.endpoints.length - 3} more
                              </Text>
                            )}
                          </View>
                        )}
                      </View>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
      
      {/* Pagination Controls */}
      {filteredMetrics.length > itemsPerPage && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16, paddingVertical: 12 }}>
          <TouchableOpacity
            onPress={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={[styles.btn, currentPage === 1 && { opacity: 0.4 }]}
          >
            <Text style={styles.btnText}>← Previous</Text>
          </TouchableOpacity>
          <Text style={{ color: '#fff', fontSize: 14, fontWeight: '600' }}>
            Page {currentPage} of {Math.ceil(filteredMetrics.length / itemsPerPage)}
          </Text>
          <TouchableOpacity
            onPress={() => setCurrentPage(p => Math.min(Math.ceil(filteredMetrics.length / itemsPerPage), p + 1))}
            disabled={currentPage >= Math.ceil(filteredMetrics.length / itemsPerPage)}
            style={[styles.btn, currentPage >= Math.ceil(filteredMetrics.length / itemsPerPage) && { opacity: 0.4 }]}
          >
            <Text style={styles.btnText}>Next →</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

function NotificationsTab() {
  const [notifications, setNotifications] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [expandedNotifId, setExpandedNotifId] = useState(null);

  // Load notifications
  useEffect(() => {
    const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setNotifications(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Load query watchers
  useEffect(() => {
    const q = query(collection(db, 'query_watchers'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setWatchers(list);
    });
    return () => unsub();
  }, []);

  // Load user data
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsub = onSnapshot(q, (snap) => {
      const userMap = {};
      snap.forEach((d) => {
        const data = d.data();
        userMap[d.id] = {
          email: data.email || d.id,
          displayName: data.displayName || null
        };
      });
      setUsers(userMap);
    });
    return () => unsub();
  }, []);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const stats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.read).length,
    read: notifications.filter(n => n.read).length,
    activeWatchers: watchers.filter(w => w.status === 'active').length,
    completedWatchers: watchers.filter(w => w.status === 'completed').length,
    expiredWatchers: watchers.filter(w => w.status === 'expired').length,
  };

  const deleteNotification = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { deleted: true, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('Failed to delete notification', e);
    }
  };

  const deleteWatcher = async (id) => {
    try {
      await updateDoc(doc(db, 'query_watchers', id), { status: 'cancelled', updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('Failed to delete watcher', e);
    }
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Notifications & Watchers {loading ? '(loading...)' : `(${notifications.length} notifications, ${watchers.length} watchers)`}</Text>
      
      {/* Stats */}
      <View style={[styles.card, { marginBottom: 16 }]}>
        <Text style={styles.groupHeader}>Statistics</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Total Notifications</Text>
            <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.total}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Unread</Text>
            <Text style={{ color: '#fbbf24', fontSize: 24, fontWeight: '700' }}>{stats.unread}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Read</Text>
            <Text style={{ color: '#4ade80', fontSize: 24, fontWeight: '700' }}>{stats.read}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Active Watchers</Text>
            <Text style={{ color: '#4CAF50', fontSize: 24, fontWeight: '700' }}>{stats.activeWatchers} <Text style={{ fontSize: 14 }}>👁️</Text></Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Completed</Text>
            <Text style={{ color: '#9ca3af', fontSize: 24, fontWeight: '700' }}>{stats.completedWatchers}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Expired</Text>
            <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>{stats.expiredWatchers}</Text>
          </View>
        </View>
      </View>

      {/* Filter */}
      <View style={[styles.row, { marginBottom: 16 }]}>
        <Text style={styles.label}>Filter:</Text>
        {['all', 'unread', 'read'].map((f) => (
          <TouchableOpacity
            key={f}
            onPress={() => setFilter(f)}
            style={[styles.tag, filter === f ? styles.tagActive : null]}
          >
            <Text style={[styles.tagText, filter === f ? styles.tagTextActive : null]}>
              {f === 'all' ? 'All' : f === 'unread' ? `Unread (${stats.unread})` : `Read (${stats.read})`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Notifications List */}
      <Text style={styles.groupHeader}>Notifications</Text>
      <ScrollView style={{ flex: 1, maxHeight: 300 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {filteredNotifications.map((n) => {
          const user = users[n.uid];
          const isExpanded = expandedNotifId === n.id;
          
          return (
            <View key={n.id} style={[styles.userRow, { flexDirection: 'column', alignItems: 'stretch' }]}>
              <TouchableOpacity 
                onPress={() => setExpandedNotifId(isExpanded ? null : n.id)}
                style={{ flex: 1 }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.userEmail, !n.read && { fontWeight: '700', color: '#fbbf24' }]}>{n.title || 'No title'}</Text>
                    <Text style={[styles.userMeta, { marginTop: 4 }]}>
                      {user?.email || n.uid || 'Unknown'} · {n.createdAt?.toDate?.() ? n.createdAt.toDate().toLocaleString() : 'Unknown date'}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    {!n.read && (
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fbbf24' }} />
                    )}
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        deleteNotification(n.id);
                      }}
                      style={{ padding: 4 }}
                    >
                      <Text style={{ color: '#f87171', fontSize: 16 }}>✕</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                {isExpanded && (
                  <View style={{ marginTop: 8, padding: 12, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 8 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginBottom: 8 }}>{n.message || 'No message'}</Text>
                    {n.fixtureTitle && (
                      <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Fixture: {n.fixtureTitle}</Text>
                    )}
                    {n.fixtureId && (
                      <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                        ID: {n.fixtureId}
                      </Text>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            </View>
          );
        })}
        {filteredNotifications.length === 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', paddingVertical: 24 }}>No notifications</Text>
        )}
      </ScrollView>

      {/* Watchers List */}
      <Text style={[styles.groupHeader, { marginTop: 24 }]}>Query Watchers</Text>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {watchers.map((w) => {
          const user = users[w.uid];
          const statusColor = w.status === 'active' ? '#4CAF50' : w.status === 'completed' ? '#9ca3af' : '#f87171';
          
          return (
            <View key={w.id} style={[styles.userRow, { borderLeftWidth: 3, borderLeftColor: statusColor }]}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <View style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: `${statusColor}20`, borderRadius: 4 }}>
                    <Text style={{ color: statusColor, fontSize: 11, fontWeight: '700' }}>{w.status?.toUpperCase()}</Text>
                  </View>
                  <Text style={styles.userEmail}>{w.fixtureTitle || 'No title'}</Text>
                </View>
                <Text style={styles.userMeta}>
                  👤 {user?.email || w.uid || 'Unknown'} · {w.createdAt?.toDate?.() ? w.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                </Text>
                {w.startDate && (
                  <Text style={[styles.userMeta, { marginTop: 2 }]}>
                    🎮 {new Date(w.startDate).toLocaleString()}
                  </Text>
                )}
                {w.lastChecked && (
                  <Text style={[styles.userMeta, { marginTop: 2, fontSize: 11 }]}>
                    Last checked: {w.lastChecked?.toDate?.() ? new Date(w.lastChecked.toDate()).toLocaleTimeString() : 'Unknown'}
                  </Text>
                )}
              </View>
              <TouchableOpacity
                onPress={() => deleteWatcher(w.id)}
                style={{ padding: 8, backgroundColor: 'rgba(244, 67, 54, 0.15)', borderRadius: 6 }}
              >
                <Text style={{ color: '#f87171', fontSize: 12 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          );
        })}
        {watchers.length === 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', paddingVertical: 24 }}>No watchers</Text>
        )}
      </ScrollView>
    </View>
  );
}

function MaintenanceTab() {
  const [comingSoon, setComingSoon] = useState(false);
  const [banner, setBanner] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Community settings
  const [communityModalEnabled, setCommunityModalEnabled] = useState(true);
  const [communityRedirectUrl, setCommunityRedirectUrl] = useState('https://discord.gg/your-discord-link');
  
  // SGP Optimizer Coming Soon settings
  const [sgpComingSoonEnabled, setSgpComingSoonEnabled] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'site_maintenance', 'global');
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      setComingSoon(!!d.comingSoonEnabled);
      setBanner(!!d.maintenanceBannerEnabled);
      setStartedAt(d.maintenanceBannerStartedAt || null);
      setCommunityModalEnabled(d.communityModalEnabled !== false); // Default to true
      setCommunityRedirectUrl(d.communityRedirectUrl || 'https://discord.gg/your-discord-link');
      setSgpComingSoonEnabled(!!d.sgpComingSoonEnabled);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const toggleComingSoon = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { comingSoonEnabled: !comingSoon, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle coming soon', e);
    }
  };

  const toggleBanner = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      const payload = { maintenanceBannerEnabled: !banner, updatedAt: serverTimestamp() };
      if (!banner && !startedAt) payload.maintenanceBannerStartedAt = serverTimestamp();
      await setDoc(ref, payload, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle banner', e);
    }
  };

  const toggleCommunityModal = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { communityModalEnabled: !communityModalEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle community modal', e);
    }
  };

  const updateCommunityUrl = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { communityRedirectUrl: communityRedirectUrl, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to update community URL', e);
    }
  };

  const toggleSgpComingSoon = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { sgpComingSoonEnabled: !sgpComingSoonEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle SGP coming soon', e);
    }
  };


  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Maintenance Controls</Text>
      <View style={styles.row}>
        <Text style={styles.label}>Coming Soon Page</Text>
        <Switch value={comingSoon} onValueChange={toggleComingSoon} disabled={loading} />
        <Text style={styles.hintText}>{comingSoon ? 'Enabled' : 'Disabled'}</Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Maintenance Banner</Text>
        <Switch value={banner} onValueChange={toggleBanner} disabled={loading} />
        <Text style={styles.hintText}>{banner ? 'Shown' : 'Hidden'}</Text>
      </View>

      {banner ? (
        <Text style={styles.caption}>
          Started: {startedAt && startedAt.toDate ? startedAt.toDate().toLocaleString() : 'now'}
        </Text>
      ) : null}

      <Text style={styles.caption}>To grant access during Coming Soon, have users sign in; admins can access the full app after login.</Text>

      <Text style={styles.caption}>Coming Soon redirects non-admins (prod only). Banner shows on all pages with start time.</Text>
      
      <Text style={styles.sectionTitle}>Community Settings</Text>
      
      <View style={styles.row}>
        <Text style={styles.label}>Community Modal</Text>
        <Switch value={communityModalEnabled} onValueChange={toggleCommunityModal} disabled={loading} />
        <Text style={styles.hintText}>{communityModalEnabled ? 'Show Modal' : 'Redirect to URL'}</Text>
      </View>

      {!communityModalEnabled ? (
        <View style={styles.row}>
          <Text style={styles.label}>Redirect URL</Text>
          <TextInput
            style={styles.input}
            value={communityRedirectUrl}
            onChangeText={setCommunityRedirectUrl}
            onBlur={updateCommunityUrl}
            placeholder="https://discord.gg/your-link"
          />
        </View>
      ) : null}

      <Text style={styles.caption}>
        When enabled: Community tab shows the community modal. When disabled: Community tab redirects to the specified URL.
      </Text>
      
      <Text style={styles.sectionTitle}>SGP Optimizer Settings</Text>
      
      <View style={styles.row}>
        <Text style={styles.label}>SGP Coming Soon Modal</Text>
        <Switch value={sgpComingSoonEnabled} onValueChange={toggleSgpComingSoon} disabled={loading} />
        <Text style={styles.hintText}>{sgpComingSoonEnabled ? 'Shown' : 'Hidden'}</Text>
      </View>

      <Text style={styles.caption}>
        When enabled: Shows "Coming Soon" modal on SGP Optimizer page for non-admin users. When disabled: All users can access the SGP Optimizer.
      </Text>
    </View>
  );
}

export default function AdminScreen() {
  // Safely get navigation context
  let navigateTo = null;
  try {
    const navigation = useNavigation();
    navigateTo = navigation?.navigateTo;
  } catch (e) {
    // Navigation context not available, that's okay
    console.log('Navigation context not available');
  }
  
  // Get ticker data from context
  const { fixtures, isTickerEnabled, isTickerCollapsed, toggleCollapsed, tickerHeight } = useTicker();
  
  const tabs = useMemo(() => ([
    { key: 'users', label: 'Users', el: <UsersTab /> },
    { key: 'metrics', label: 'Metrics', el: <MetricsTab /> },
    { key: 'cache', label: 'Odds Cache', el: <CacheMonitoringTab /> },
    { key: 'sgp', label: 'SGP Cache', el: <SGPCacheTab /> },
    { key: 'health', label: 'Site Health', el: <SiteHealthTab /> },
    { key: 'notifications', label: 'Notifications', el: <NotificationsTab /> },
    { key: 'email', label: 'Email', el: <EmailTestingTab /> },
    { key: 'api', label: 'API', el: <ApiTab /> },
    { key: 'ai', label: 'bOPT AI', el: <AiTab /> },
    { key: 'maintenance', label: 'Maintenance', el: <MaintenanceTab /> },
  ]), []);
  
  const [tab, setTab] = useState('users');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect screen size for mobile menu
  useEffect(() => {
    const checkScreenSize = () => {
      if (Platform.OS === 'web') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    
    checkScreenSize();
    
    if (Platform.OS === 'web') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);
  
  // URL routing support - check URL hash on mount and changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window?.location?.hash?.substring(1); // Remove '#'
      if (hash && hash.startsWith('admin/')) {
        const tabKey = hash.split('/')[1];
        if (tabs.find(t => t.key === tabKey)) {
          setTab(tabKey);
        }
      }
    };
    
    // Check initial hash
    handleHashChange();
    
    // Listen for hash changes
    window?.addEventListener?.('hashchange', handleHashChange);
    return () => window?.removeEventListener?.('hashchange', handleHashChange);
  }, [tabs]);
  
  // Update URL when tab changes
  const handleTabChange = (tabKey) => {
    setTab(tabKey);
    setMenuOpen(false); // Close menu on mobile
    if (window?.history?.pushState) {
      window.history.pushState(null, null, `#admin/${tabKey}`);
    }
  };
  
  // Back to home function
  const handleBackToHome = () => {
    if (navigateTo) {
      navigateTo('Home');
    } else {
      // Fallback: navigate via URL if navigation context not available
      if (typeof window !== 'undefined') {
        window.location.href = '/results';
      }
    }
  };

  return (
    <View style={styles.container}>
      {/* Game Ticker Only (no navbar) */}
      {isTickerEnabled && (
        <GameTicker 
          fixtures={fixtures}
          isCollapsed={isTickerCollapsed}
          onCollapseToggle={toggleCollapsed}
        />
      )}
      
      <View style={styles.headerContainer}>
        {/* Hamburger menu button for small screens */}
        {isMobile && (
          <TouchableOpacity style={styles.hamburgerBtn} onPress={() => setMenuOpen(!menuOpen)}>
            <Text style={styles.hamburgerIcon}>{menuOpen ? '✕' : '☰'}</Text>
          </TouchableOpacity>
        )}
        
        {/* Desktop tabs - hidden on small screens */}
        {!isMobile && (
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.desktopTabs}
            contentContainerStyle={styles.headerContent}
          >
        {tabs.map(t => (
              <TabButton key={t.key} label={t.label} active={tab === t.key} onPress={() => handleTabChange(t.key)} />
        ))}
          </ScrollView>
        )}
        
        {/* Current tab label on mobile */}
        {isMobile && !menuOpen && (
          <Text style={styles.currentTabLabel}>
            {tabs.find(t => t.key === tab)?.label || 'Admin'}
          </Text>
        )}
        
        <TouchableOpacity style={styles.backHomeBtn} onPress={handleBackToHome}>
          <Text style={styles.backHomeBtnText}>← Home</Text>
        </TouchableOpacity>
      </View>
      
      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <View style={styles.mobileMenu}>
          {tabs.map(t => (
            <TouchableOpacity 
              key={t.key} 
              style={[styles.mobileMenuItem, tab === t.key && styles.mobileMenuItemActive]} 
              onPress={() => handleTabChange(t.key)}
            >
              <Text style={[styles.mobileMenuText, tab === t.key && styles.mobileMenuTextActive]}>
                {t.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      
      <View style={styles.body}>
        {tabs.find(t => t.key === tab)?.el}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0b0b0b' },
  headerContainer: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    borderBottomWidth: 1, 
    borderBottomColor: 'rgba(255,255,255,0.08)',
    backgroundColor: '#0b0b0b',
    position: 'relative',
    marginTop: 8,
  },
  hamburgerBtn: {
    padding: 12,
    marginRight: 8,
  },
  hamburgerIcon: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '600',
  },
  desktopTabs: { 
    flex: 1,
  },
  currentTabLabel: {
    flex: 1,
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
  },
  headerContent: {
    flexDirection: 'row',
    padding: 12,
    gap: 8,
    paddingRight: 120, // Space for back button
  },
  backHomeBtn: {
    position: 'absolute',
    right: 12,
    top: 12,
    bottom: 12,
    backgroundColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  backHomeBtnText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 14,
    fontWeight: '600',
  },
  mobileMenu: {
    backgroundColor: '#1a1a1a',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  mobileMenuItem: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  mobileMenuItemActive: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  mobileMenuText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 16,
    fontWeight: '500',
  },
  mobileMenuTextActive: {
    color: '#fff',
    fontWeight: '700',
  },
  body: { flex: 1, padding: 16 },
  tabBtn: { 
    paddingVertical: 10, 
    paddingHorizontal: 14, 
    borderRadius: 8, 
    borderWidth: 1, 
    borderColor: 'rgba(255,255,255,0.14)', 
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  tabBtnActive: { 
    backgroundColor: 'rgba(255,255,255,0.10)', 
    borderColor: 'rgba(255,255,255,0.28)',
  },
  tabBtnText: { 
    color: 'rgba(255,255,255,0.8)', 
    fontWeight: '600',
  },
  tabBtnTextActive: { 
    color: '#fff',
  },
  tabWrap: { flex: 1 },
  sectionTitle: { color: '#fff', fontSize: 18, fontWeight: '700', marginBottom: 12 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  label: { color: 'rgba(255,255,255,0.9)', width: 100 },
  input: { flex: 1, height: 40, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', color: '#fff' },
  btn: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: '#ffffff', alignItems: 'center' },
  btnText: { color: '#000', fontWeight: '700' },
  outputBox: { flex: 1, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  outputText: { color: 'rgba(255,255,255,0.85)', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, whiteSpace: Platform.OS === 'web' ? 'pre-wrap' : undefined },
  userRow: { flexDirection: 'column', alignItems: 'stretch', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', marginBottom: 8 },
  userRowMobile: { flexDirection: 'column', alignItems: 'stretch', paddingVertical: 12 },
  userEmail: { color: '#fff', fontWeight: '600' },
  userMeta: { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  userActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', maxWidth: '100%' },
  actionGroupLabel: { color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600', marginBottom: 6, textTransform: 'uppercase' },
  smallBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.14)' },
  smallBtnMobile: { paddingVertical: 6, paddingHorizontal: 8 },
  smallBtnText: { color: '#fff', fontWeight: '600', fontSize: 12 },
  
  // Mobile Dropdown Styles
  mobileDropdownGroup: {
    marginBottom: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    overflow: 'hidden',
  },
  mobileDropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  mobileDropdownHeaderText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  mobileDropdownArrow: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
  },
  mobileDropdownContent: {
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  mobileDropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  mobileDropdownItemText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '500',
  },
  hintText: { color: 'rgba(255,255,255,0.8)' },
  caption: { color: 'rgba(255,255,255,0.7)', marginTop: 8 },
  groupHeader: { color: 'rgba(255,255,255,0.9)', fontWeight: '700', marginTop: 12, marginBottom: 6 },
  card: { padding: 12, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.03)', marginBottom: 12 },
  tag: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
  tagActive: { backgroundColor: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.28)' },
  tagText: { color: 'rgba(255,255,255,0.85)', fontWeight: '600' },
  tagTextActive: { color: '#000' },
  link: { color: '#9fd3ff', textDecorationLine: 'underline' },
  betRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
  betMain: { color: '#fff', fontWeight: '700' },
  betMeta: { color: 'rgba(255,255,255,0.7)' }
});


